import { useState, useEffect, useCallback, useRef } from 'react';
import SwiperType from 'swiper';

interface UseNavigationDotsProps {
  swiper: SwiperType | null;
  totalSlides: number;
  autoHide?: boolean;
}

interface UseNavigationDotsReturn {
  currentSlide: number;
  isVisible: boolean;
  handleDotClick: (index: number) => void;
  showDots: () => void;
  hideDots: () => void;
  toggleDotsVisibility: () => void;
}

export const useNavigationDots = ({
  swiper,
  totalSlides,
  autoHide = true, // Default to true for backward compatibility
}: UseNavigationDotsProps): UseNavigationDotsReturn => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  // Auto-hide timeout reference - only used when autoHide is enabled
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Create stable timer functions that don't cause re-renders
  const clearHideTimer = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const resetHideTimer = useCallback(() => {
    if (!autoHide) return; // Skip timer if auto-hide is disabled
    
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    const timeout = setTimeout(() => {
      setIsVisible(false);
    }, 3000);
    hideTimeoutRef.current = timeout;
  }, [autoHide]);

  // Update current slide when swiper changes
  useEffect(() => {
    if (!swiper) return;

    const handleSlideChange = () => {
      setCurrentSlide(swiper.activeIndex);
      if (autoHide) {
        // Show dots temporarily when slide changes (only if auto-hide is enabled)
        setIsVisible(true);
        resetHideTimer();
      }
    };

    const handleTouchStart = () => {
      setIsVisible(true);
      if (autoHide) {
        clearHideTimer();
      }
    };

    const handleTouchEnd = () => {
      if (autoHide) {
        resetHideTimer();
      }
    };

    // Set initial slide
    setCurrentSlide(swiper.activeIndex);

    // Add event listeners
    swiper.on('slideChange', handleSlideChange);
    
    if (autoHide) {
      swiper.on('touchStart', handleTouchStart);
      swiper.on('touchEnd', handleTouchEnd);
    }

    return () => {
      swiper.off('slideChange', handleSlideChange);
      if (autoHide) {
        swiper.off('touchStart', handleTouchStart);
        swiper.off('touchEnd', handleTouchEnd);
      }
    };
  }, [swiper, autoHide, resetHideTimer, clearHideTimer]);

  // Handle dot click
  const handleDotClick = useCallback(
    (index: number) => {
      if (swiper && index !== currentSlide) {
        swiper.slideTo(index);
        setCurrentSlide(index);
      }
      if (autoHide) {
        // Show dots when interacting (only if auto-hide is enabled)
        setIsVisible(true);
        resetHideTimer();
      }
    },
    [swiper, currentSlide, autoHide, resetHideTimer]
  );

  // Manual visibility controls
  const showDots = useCallback(() => {
    setIsVisible(true);
    if (autoHide) {
      resetHideTimer();
    }
  }, [autoHide, resetHideTimer]);

  const hideDots = useCallback(() => {
    setIsVisible(false);
    if (autoHide) {
      clearHideTimer();
    }
  }, [autoHide, clearHideTimer]);

  const toggleDotsVisibility = useCallback(() => {
    if (isVisible) {
      hideDots();
    } else {
      showDots();
    }
  }, [isVisible, showDots, hideDots]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearHideTimer();
    };
  }, [clearHideTimer]);

  // Set visibility based on slide count and auto-hide setting
  useEffect(() => {
    if (totalSlides <= 1) {
      setIsVisible(false);
      clearHideTimer();
    } else {
      setIsVisible(true);
      if (autoHide) {
        resetHideTimer();
      }
    }
  }, [totalSlides, autoHide, clearHideTimer, resetHideTimer]);

  return {
    currentSlide,
    isVisible,
    handleDotClick,
    showDots,
    hideDots,
    toggleDotsVisibility,
  };
};